import React, { useState } from "react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { Popover } from "antd";
import { MessageReaction } from "../../../models/EdnaChat";
import { useStore } from "../../../stores";
import styles from "./MessageReactions.module.scss";
import { MdOutlineEmojiEmotions } from "react-icons/md";

interface MessageReactionsProps {
  messageId: string;
  isFromUser: boolean;
  reactions?: Record<string, MessageReaction[]>;
}

const commonEmojis = ["👍", "👎", "❤️", "😂", "😮", "🎉", "🙏", "👀"];

const MessageReactionsPopover: React.FC<MessageReactionsProps> = ({
  messageId,
  isFromUser,
  reactions,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loadingEmoji, setLoadingEmoji] = useState<string | null>(null);
  const { toggleReaction } = useStore("ednaChats");
  const { user } = useStore("auth");

  const handleEmojiClick = async (emoji: string) => {
    setLoadingEmoji(emoji);
    try {
      await toggleReaction(messageId, emoji);
      setShowEmojiPicker(false);
    } finally {
      setLoadingEmoji(null);
    }
  };

  return (
    <div
      className={clsx(styles.addReactionsContainer, "add-reaction-container")}
      style={{
        opacity: showEmojiPicker ? 1 : undefined,
        right: isFromUser ? "-30px" : "auto",
        left: !isFromUser ? "-30px" : "auto",
      }}
    >
      <Popover
        content={
          <div className={styles.emojiPicker}>
            {commonEmojis.map((emoji) => {
              const hasUserReacted = user
                ? (reactions?.[emoji] ?? []).some((r) => r.userId === user.id)
                : false;

              return (
                <button
                  key={emoji}
                  className={clsx(styles.emojiButton, {
                    [styles.userReacted]: hasUserReacted,
                  })}
                  onClick={() => handleEmojiClick(emoji)}
                  disabled={loadingEmoji !== null}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        }
        trigger="click"
        open={showEmojiPicker}
        onOpenChange={setShowEmojiPicker}
        placement="top"
      >
        <button
          className={styles.addReactionButton}
          aria-label="Add reaction"
          disabled={loadingEmoji !== null}
        >
          <MdOutlineEmojiEmotions />
        </button>
      </Popover>
    </div>
  );
};

export default observer(MessageReactionsPopover);
